import type { Resource } from '@/types';

/**
 * Data-visibility scope applied to a report, driven by the logged-in user's
 * role → report permission ("View Access"):
 *   - 'all'  → every record
 *   - 'team' → records belonging to the user's department
 *   - 'own'  → the user's own records only
 */
export type ReportViewAccess = 'own' | 'team' | 'all';

const normalise = (value?: string | null): string => value?.trim().toLowerCase() ?? '';

/**
 * Resolve the resource record that represents the logged-in user, matched by
 * email (case-insensitive). Mirrors the matching used across the app (e.g.
 * Resources directory scoping).
 */
export function findOwnResource(resources: Resource[], userEmail?: string): Resource | undefined {
  const email = normalise(userEmail);
  if (!email) return undefined;
  return resources.find((r) => normalise(r.email) === email);
}

/**
 * Scope a list of resources to the caller's report view-access level:
 *   - 'all'  → every resource (unchanged)
 *   - 'own'  → only the caller's own resource record
 *   - 'team' → resources sharing the caller's department (falls back to the own
 *              record when the department is unknown)
 * When the caller cannot be matched to a resource, non-'all' levels return an
 * empty list so no unauthorised data leaks through.
 */
export function scopeResourcesByAccess(
  resources: Resource[],
  access: ReportViewAccess,
  userEmail?: string,
): Resource[] {
  if (access === 'all') return resources;

  const own = findOwnResource(resources, userEmail);
  if (!own) return [];

  if (access === 'own') return [own];

  // 'team' — resources in the same department as the logged-in user
  const dept = normalise(own.department);
  if (!dept) return [own];
  return resources.filter((r) => normalise(r.department) === dept);
}

/**
 * Build the set of user identities (names + emails, lowercased) visible at a
 * given access level. Used by activity-style reports (e.g. Audit Log) that key
 * off the acting user's name/email rather than a resource list.
 *
 * Returns `null` for 'all' — meaning "no restriction, show every record".
 */
export function scopedUserIdentities(
  resources: Resource[],
  access: ReportViewAccess,
  user: { name?: string; email?: string },
): Set<string> | null {
  if (access === 'all') return null;

  const ids = new Set<string>();
  const add = (value?: string | null) => {
    const s = normalise(value);
    if (s) ids.add(s);
  };

  // The logged-in user always sees their own activity.
  add(user.name);
  add(user.email);
  const own = findOwnResource(resources, user.email);
  add(own?.name);
  add(own?.email);

  if (access === 'team') {
    const scoped = scopeResourcesByAccess(resources, 'team', user.email);
    for (const r of scoped) {
      add(r.name);
      add(r.email);
    }
  }

  return ids;
}
